import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface TattooArtistTemplateProps {
    profile: PublicProfile
}

export function TattooArtistTemplate({ profile }: TattooArtistTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center overflow-hidden" style={{ backgroundColor: colors.colors.background }}>
                <div className="absolute inset-0">
                    {profile.cover_image ? (
                        <img
                            src={profile.cover_image}
                            alt=""
                            className="w-full h-full object-cover opacity-40"
                        />
                    ) : profile.profile_image ? (
                        <img
                            src={profile.profile_image}
                            alt=""
                            className="w-full h-full object-cover opacity-40"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                </div>
                {/* Neon glow effects */}
                <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.accent + '33' }} />
                <div className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.secondary + '33' }} />

                <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:px-6 md:py-24 lg:px-8">
                    <div className="max-w-3xl">
                        {profile.tagline && (
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.secondary }}
                            >
                                {profile.tagline}
                            </p>
                        )}
                        <h1
                            className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6"
                            style={{ fontFamily: typography.heading }}
                        >
                            YOUR SKIN, <br />
                            <span
                                className="bg-clip-text"
                                style={{
                                    backgroundImage: `linear-gradient(to right, ${colors.colors.accent}, ${colors.colors.secondary})`,
                                    color: 'transparent',
                                }}
                            >
                                {t('template.tattoo.myCanvas')}
                            </span>
                        </h1>
                        <p className="text-xl mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                            {profile.bio || t('template.tattoo.specializingInCustomDesigns')}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <button
                                onClick={() => setIsBookingOpen(true)}
                                className="inline-flex items-center px-8 py-4 rounded-full font-bold transition-all hover:opacity-90"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                            >
                                {t('template.tattoo.bookConsultation')}
                            </button>
                            <a
                                href="#work"
                                className="inline-flex items-center px-8 py-4 border-2 rounded-full font-bold transition-all hover:opacity-90"
                                style={{ borderColor: colors.colors.text, color: colors.colors.text }}
                            >
                                {t('template.tattoo.viewPortfolio')}
                            </a>
                        </div>
                        {profile.location && (
                            <p className="text-sm mt-6" style={{ color: colors.colors.textMuted }}>
                                📍 {profile.location}
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* Portfolio/Gallery Section */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="work" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.secondary }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.tattoo.portfolio')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-black"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.tattoo.recentWork')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={8}
                            enableLightbox
                            showCaptions
                            aspectRatio="portrait"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* About Section */}
            <section id="about" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-2xl shadow-2xl w-full object-cover grayscale"
                                />
                            ) : (
                                <div
                                    className="rounded-2xl shadow-2xl w-full aspect-square flex items-center justify-center grayscale"
                                    style={{ backgroundColor: colors.colors.secondary }}
                                >
                                    <span
                                        className="text-6xl font-bold"
                                        style={{ color: colors.colors.primary }}
                                    >
                                        {profile.business_name[0]}
                                    </span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                            >
                                <p className="text-4xl font-black">8+</p>
                                <p className="text-sm font-bold">{t('template.tattoo.yearsOfInk')}</p>
                            </div>
                        </div>
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.secondary }}
                            >
                                {t('template.tattoo.about')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-black mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.tattoo.theArtistBehindTheNeedle')}
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="flex items-center space-x-4 mt-6">
                                    {profile.social_links.map((link) => (
                                        <a
                                            key={link.platform}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Follow on ${link.platform}`}
                                            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                            style={{ backgroundColor: colors.colors.text + '1A', color: colors.colors.secondary }}
                                        >
                                            <span className="text-lg">
                                                {link.platform === 'instagram' && '📸'}
                                                {link.platform === 'tiktok' && '🎵'}
                                                {!['instagram', 'tiktok'].includes(link.platform) && '🔗'}
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services/Pricing Section */}
            {profile.services && profile.services.length > 0 && (
                <section id="services" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.secondary }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.tattoo.services')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-black"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.tattoo.pricingAndInfo')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-2xl p-8 shadow-lg border-2 border-transparent hover:border-current transition-colors"
                                    style={{
                                        borderColor: index === 2 ? colors.colors.accent : 'transparent',
                                    }}
                                >
                                    <div className="flex justify-between items-start mb-6">
                                        <div
                                            className="w-16 h-16 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : colors.colors.accent }}>
                                                {index === 0 ? '🖊️' : index === 1 ? '✒️' : index === 2 ? '👑' : index === 3 ? '🔄' : '💉'}
                                            </span>
                                        </div>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="text-3xl font-black" style={{ color: colors.colors.text }}>
                                                ${service.price.toFixed(0)}
                                                <span className="text-sm font-mono" style={{ color: colors.colors.textMuted }}>/hr</span>
                                            </span>
                                        )}
                                    </div>
                                    <h3
                                        className="text-xl font-black mb-3"
                                        style={{ fontFamily: typography.heading, color: colors.colors.text }}
                                    >
                                        {service.name.toUpperCase()}
                                    </h3>
                                    {service.description && (
                                        <p className="mb-4" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm" style={{ color: colors.colors.textMuted }}>
                                            <span className="mr-2" style={{ color: colors.colors.accent }}>⏱</span>
                                            <span>{service.duration_minutes} min</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Availability Section */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.tattoo.studioHours')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-black"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.tattoo.bookingHours')}
                            </h2>
                        </div>
                        <div className="rounded-3xl p-8 md:p-12" style={{ backgroundColor: colors.colors.secondary }}>
                            <div className="grid md:grid-cols-2 gap-8">
                                {profile.availability.slice(0, 7).map((day) => (
                                    <div
                                        key={day.day_of_week}
                                        className="flex justify-between items-center py-3 border-b"
                                        style={{ borderColor: colors.colors.text + '1A' }}
                                    >
                                        <span className="font-bold" style={{ color: colors.colors.text }}>
                                            {day.day_name}
                                        </span>
                                        <span style={{ color: colors.colors.textMuted }}>
                                            {day.is_available
                                                ? day.is_all_day
                                                    ? t('template.common.allDay')
                                                    : `${day.start_time} - ${day.end_time}`
                                                : t('template.common.closed')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 text-center rounded-xl p-6" style={{ backgroundColor: colors.colors.accent + '1A' }}>
                                <p className="font-mono text-sm" style={{ color: colors.colors.text }}>
                                    <span className="mr-2" style={{ color: colors.colors.accent }}>ℹ️</span>
                                    {t('template.tattoo.walkInsWelcome')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section id="contact" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.secondary }}>
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.tattoo.contact')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-black mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.tattoo.letsCreateTogether')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {t('template.tattoo.readyToGetInked')}
                            </p>
                            <div className="space-y-6">
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.secondary }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold" style={{ color: colors.colors.text }}>{t('template.tattoo.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold" style={{ color: colors.colors.text }}>{t('template.tattoo.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold" style={{ color: colors.colors.text }}>{t('template.tattoo.studioLocation')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <form className="bg-white rounded-3xl p-8 shadow-xl">
                                <h3 className="text-2xl font-black mb-6" style={{ fontFamily: typography.heading, color: colors.colors.text }}>
                                    {t('template.tattoo.requestAppointment')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-bold mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.tattoo.name')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl border-2 outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder={t('template.tattoo.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.tattoo.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl border-2 outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.tattoo.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-4 py-4 rounded-xl border-2 outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.tattoo.describeYourIdea')}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-4 rounded-xl border-2 outline-none transition-all resize-none"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder={t('template.tattoo.tellMeAboutYourTattooIdea')}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-4 rounded-xl font-black text-center transition-all hover:opacity-90"
                                        style={{ backgroundColor: colors.colors.background, color: colors.colors.text }}
                                    >
                                        {t('template.tattoo.submitRequest')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-6 lg:px-8 text-center border-t" style={{ backgroundColor: colors.colors.background, borderColor: colors.colors.text + '1A' }}>
                <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border-2"
                    style={{ borderColor: colors.colors.accent }}
                >
                    <span style={{ color: colors.colors.accent }}>🖊️</span>
                </div>
                <h3 className="text-2xl font-black tracking-wider mb-2" style={{ fontFamily: typography.heading, color: colors.colors.text }}>
                    {profile.business_name.toUpperCase()}
                </h3>
                <p
                    className="text-sm uppercase tracking-widest mb-6"
                    style={{ color: colors.colors.accent }}
                >
                    {t('template.tattoo.tattooArtist')}
                </p>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Follow on ${link.platform}`}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                style={{ backgroundColor: colors.colors.text + '1A', color: colors.colors.accent }}
                            >
                                <span className="text-sm">
                                    {link.platform === 'instagram' && '📸'}
                                    {link.platform === 'tiktok' && '🎵'}
                                    {!['instagram', 'tiktok'].includes(link.platform) && '🔗'}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm font-mono" style={{ color: colors.colors.textMuted }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.tattoo.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default TattooArtistTemplate;
